import { useState, useCallback, useRef } from "react";
import Map, { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTranslation } from "react-i18next";
import { Search, MapPin, Loader2, Crosshair, Check } from "lucide-react";
import { toast } from "sonner";

const MAPBOX_TOKEN = "pk.eyJ1IjoidmlnbmExYiIsImEiOiJjbW96Y2pkOW4xMTRmMnNzZTM5b2pqMHUyIn0.PA3kmfB1x98rvbv2PquAPg";

export default function MapPicker({ initialCenter, initialZoom = 6, onConfirm, onCancel, locationLabel = "" }) {
  const { t } = useTranslation();
  const LAYERS = {
    satellite: { label: t("map.layerSatellite"), style: "mapbox://styles/mapbox/satellite-streets-v12" },
    street: { label: t("map.layerStreet"), style: "mapbox://styles/mapbox/streets-v12" },
    outdoors: { label: t("map.layerOutdoors"), style: "mapbox://styles/mapbox/outdoors-v12" },
  };
  const mapRef = useRef(null);
  const [position, setPosition] = useState(initialCenter || null);
  const [layer, setLayer] = useState("satellite");
  const [searchQuery, setSearchQuery] = useState(locationLabel || "");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [viewState, setViewState] = useState({
    longitude: initialCenter ? initialCenter[1] : 12.5,
    latitude: initialCenter ? initialCenter[0] : 42.5,
    zoom: initialCenter ? 15 : initialZoom,
  });

  const flyTo = useCallback((lng, lat, zoom = 15) => {
    const m = mapRef.current?.getMap?.();
    if (m) m.flyTo({ center: [lng, lat], zoom, duration: 1200, essential: true });
    else setViewState({ longitude: lng, latitude: lat, zoom });
  }, []);

  const onMapLoad = useCallback(() => {
    const m = mapRef.current?.getMap?.();
    if (!m) return;
    m.getCanvas().style.cursor = "crosshair";
    m.on("click", (e) => {
      const { lng, lat } = e.lngLat;
      setPosition([lat, lng]);
    });
  }, []);

  const search = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    setResults([]);
    try {
      const geocodingLanguage = localStorage.getItem("userCountryLang") === "fr" ? "fr" : "it";
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchQuery)}.json?access_token=${MAPBOX_TOKEN}&language=${geocodingLanguage}&limit=5`;
      const r = await fetch(url);
      const data = await r.json();
      if (!data.features || data.features.length === 0) {
        toast.error(t("map.addressNotFound"));
        return;
      }
      setResults(data.features);
      const first = data.features[0];
      const [lng, lat] = first.center;
      flyTo(lng, lat, 15);
    } catch (err) {
      toast.error(t("map.searchError"));
    } finally {
      setSearching(false);
    }
  };

  const pickFromList = (feature) => {
    const [lng, lat] = feature.center;
    flyTo(lng, lat, 15);
    setResults([]);
  };

  const useGPS = () => {
    if (!navigator.geolocation) { toast.error(t("map.geoNotSupported")); return; }
    toast.info(t("map.geoLocating"));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        flyTo(lng, lat, 17);
        setPosition([lat, lng]);
        toast.success(t("map.geoFound"));
      },
      () => toast.error(t("map.geoError")),
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  const confirm = () => {
    if (!position) { toast.error(t("map.tapRequired")); return; }
    onConfirm({
      latitude: position[0],
      longitude: position[1],
      location_name: searchQuery || locationLabel || `${position[0].toFixed(4)}, ${position[1].toFixed(4)}`,
    });
  };

  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: "var(--bg)", zIndex: 9999 }} data-testid="map-picker">
      <div className="px-3 py-2 flex items-center gap-2 shadow-sm" style={{ background: "var(--surface)", borderBottom: "2px solid var(--border)" }}>
        <button type="button" onClick={onCancel} className="vs-btn-secondary px-3 py-2 text-sm" data-testid="map-cancel">
          {t("map.cancelButton")}
        </button>
        <form onSubmit={search} className="flex-1 flex gap-2">
          <div className="flex-1 relative">
            <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
            <input className="vs-input" style={{ paddingLeft: 36 }} placeholder={t("map.searchPlaceholder")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="map-search-input" />
          </div>
          <button type="submit" className="vs-btn-primary px-4" disabled={searching} style={{ background: "var(--gradient-vine)", borderColor: "var(--primary)" }} data-testid="map-search-btn">
            {searching ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />}
          </button>
        </form>
      </div>

      {results.length > 1 && (
        <div className="px-3 py-2" style={{ background: "var(--surface)", borderBottom: "1px solid var(--border)" }}>
          <div className="text-xs font-bold mb-1" style={{ color: "var(--text-muted)" }}>
            {t("map.resultsCount", { count: results.length })}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {results.map((r, i) => (
              <button key={i} type="button" onClick={() => pickFromList(r)} className="px-2 py-1 rounded text-xs" style={{ background: "var(--secondary)", border: "1px solid var(--border)" }} data-testid={`map-result-${i}`}>
                {r.place_name.length > 50 ? r.place_name.slice(0, 50) + "..." : r.place_name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="px-3 py-2 text-sm flex items-center gap-2" style={{ background: "var(--gold)", color: "#3D2300" }}>
        <MapPin size={16} />
        <span><strong>{t("map.tapInstruction")}</strong> {t("map.tapInstructionSuffix", { layer: LAYERS[layer].label })}</span>
      </div>

      <div className="flex-1 relative" style={{ minHeight: 0 }}>
        <Map ref={mapRef} {...viewState} onMove={(evt) => setViewState(evt.viewState)} onLoad={onMapLoad} mapStyle={LAYERS[layer].style} mapboxAccessToken={MAPBOX_TOKEN} style={{ height: "100%", width: "100%" }} attributionControl={false}>
          <NavigationControl position="top-left" />
          {position && (
            <Marker longitude={position[1]} latitude={position[0]} anchor="bottom">
              <div style={{ width: 36, height: 36, background: "#C04A2F", border: "3px solid white", borderRadius: "50% 50% 50% 0", transform: "rotate(-45deg)", boxShadow: "0 2px 8px rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 12, height: 12, background: "white", borderRadius: "50%", transform: "rotate(45deg)" }} />
              </div>
            </Marker>
          )}
        </Map>

        <div className="absolute top-4 right-4 flex gap-1 rounded-xl shadow-lg overflow-hidden" style={{ background: "var(--surface)", border: "2px solid var(--primary)", zIndex: 10 }} data-testid="map-layer-selector">
          {Object.entries(LAYERS).map(([key, val], idx) => (
            <button key={key} type="button" onClick={() => setLayer(key)} className="px-2.5 py-2 text-[11px] font-extrabold" style={{ background: layer === key ? "var(--gradient-vine)" : "transparent", color: layer === key ? "white" : "var(--primary)", borderLeft: idx > 0 ? "2px solid var(--border)" : "none" }}>
              {val.label}
            </button>
          ))}
        </div>

        <button type="button" onClick={useGPS} className="absolute bottom-4 right-4 p-3 rounded-full shadow-lg" style={{ background: "var(--surface)", border: "2px solid var(--primary)", zIndex: 10 }} aria-label={t("map.gpsAriaLabel")} data-testid="map-gps">
          <Crosshair size={20} style={{ color: "var(--primary)" }} />
        </button>
      </div>

      <div className="px-3 py-3 flex flex-col gap-2" style={{ background: "var(--surface)", borderTop: "2px solid var(--border)" }}>
        {position ? (
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            <MapPin size={12} className="inline mr-1" />
            {t("map.positionLabel", { lat: position[0].toFixed(5), lng: position[1].toFixed(5) })}
          </div>
        ) : (
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            {t("map.tapToPin")}
          </div>
        )}
        <button type="button" onClick={confirm} disabled={!position} className="vs-btn-primary w-full inline-flex items-center justify-center gap-2" style={{ background: position ? "var(--gradient-vine)" : "var(--secondary)", borderColor: "var(--primary)" }} data-testid="map-confirm">
          <Check size={18} /> {t("map.confirmButton")}
        </button>
      </div>
    </div>
  );
}
